/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.forms['your-product-form-id'].addEventListener('variant:change', function(event) {
 *   var variant = event.detail.variant;
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

(function(global) {
  const DEFAULT_BREAKPOINTS = {
    sm: 375,
    md: 700,
    lg: 1280,
  };

  /**
   * Swiper transition speed (ms). Adjust only here unless you add data-speed-xs/sm/md/lg on a swiper (those win).
   * null = no value for that tier (Swiper’s default applies where nothing else is set).
   */
  const CUSTOM_SWIPER_SPEED_MS = {
    xs: 450,
    sm: 450,
    md: null,
    lg: null,
  };

  function parseNumber(value) {
    if (value === undefined || value === null || value === '') return undefined;
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
  }

  function parseBoolean(value, fallback) {
    if (value === undefined || value === null || value === '') return fallback;
    return String(value).trim().toLowerCase() === 'true';
  }

  function parseSlidesPerView(value) {
    if (value === undefined || value === null || value === '') return undefined;
    if (String(value).trim().toLowerCase() === 'auto') return 'auto';
    return parseNumber(value);
  }

  function normalizeGroup(value) {
    return value === undefined ? undefined : Math.max(1, value);
  }

  function normalizeSpace(value) {
    return value === undefined ? undefined : Math.max(0, value);
  }

  const UNBLEED_BREAKPOINTS = {
    'sm:unbleed': 700,
    'md:unbleed': 1000,
    'lg:unbleed': 1150,
  };

  function getBleedOffset(el) {
    const bleed = parseFloat(getComputedStyle(el).getPropertyValue('--distance-to-bleed'));
    return Number.isFinite(bleed) && bleed > 0 ? bleed : 0;
  }

  function getUnbleedBreakpointWidth(el) {
    const widths = Object.entries(UNBLEED_BREAKPOINTS)
      .filter(([className]) => el.classList.contains(className))
      .map(([, width]) => width);

    return widths.length ? Math.min(...widths) : undefined;
  }

  function clearSlidesOffset(options) {
    delete options.slidesOffsetAfter;
    delete options.slidesOffsetBefore;
  }

  function applySlidesOffsetAfter(el, options, loop) {
    if (loop) {
      clearSlidesOffset(options);
      Object.values(options.breakpoints || {}).forEach(clearSlidesOffset);
      return;
    }

    if (options.slidesOffsetAfter === undefined) {
      const useBleedOffset = el.classList.contains('bleed')
        || parseBoolean(el.dataset.swiperOffsetAfterBleed, false);

      if (useBleedOffset) {
        const bleedOffset = getBleedOffset(el);
        if (bleedOffset > 0) {
          options.slidesOffsetAfter = bleedOffset;
        }
      }
    }

    const unbleedAt = getUnbleedBreakpointWidth(el);
    if (unbleedAt === undefined) {
      return;
    }

    options.breakpoints = options.breakpoints || {};
    const tier = options.breakpoints[unbleedAt] || {};
    if (tier.slidesOffsetAfter === undefined) {
      options.breakpoints[unbleedAt] = { ...tier, slidesOffsetAfter: 0 };
    }
  }

  function resolveSpeedMs(datasetValue, tierKey) {
    const parsed = parseNumber(datasetValue);
    if (parsed !== undefined) return Math.max(0, parsed);
    const fallback = CUSTOM_SWIPER_SPEED_MS[tierKey];
    if (fallback === null || fallback === undefined || fallback === '') return undefined;
    const n = Number(fallback);
    return Number.isFinite(n) ? Math.max(0, n) : undefined;
  }

  function getResponsiveOptions(el) {
    const d = el.dataset;
    const options = {};
    const breakpoints = {};
    const autoSlideSize = parseBoolean(d.swiperAutoSlideSize, false);
    const autoSlidesPerGroup = parseBoolean(d.swiperSlidesPerGroupAuto, true);
    const baseSlidesPerView = parseSlidesPerView(d.spvXs);
    const baseSlidesPerGroup = normalizeGroup(parseNumber(d.spgXs));
    const baseSpaceBetween = normalizeSpace(parseNumber(d.spaceXs));
    const baseOffsetAfter = normalizeSpace(parseNumber(d.offsetAfterXs));

    options.slidesPerView = baseSlidesPerView === undefined ? 1 : baseSlidesPerView;
    if (baseSlidesPerGroup !== undefined) options.slidesPerGroup = baseSlidesPerGroup;
    if (baseSpaceBetween !== undefined) options.spaceBetween = baseSpaceBetween;
    if (baseOffsetAfter !== undefined) options.slidesOffsetAfter = baseOffsetAfter;

    const baseSpeed = resolveSpeedMs(d.speedXs, 'xs');
    if (baseSpeed !== undefined) options.speed = baseSpeed;

    [
      ['sm', d.bpSm, 'spvSm', 'spgSm', 'spaceSm', 'offsetAfterSm', 'speedSm'],
      ['md', d.bpMd, 'spvMd', 'spgMd', 'spaceMd', 'offsetAfterMd', 'speedMd'],
      ['lg', d.bpLg, 'spvLg', 'spgLg', 'spaceLg', 'offsetAfterLg', 'speedLg'],
    ].forEach(([name, breakpointValue, spvKey, spgKey, spaceKey, offsetAfterKey, speedKey]) => {
      const width = parseNumber(breakpointValue) || DEFAULT_BREAKPOINTS[name];
      const tier = {};
      const slidesPerView = parseSlidesPerView(d[spvKey]);
      const slidesPerGroup = normalizeGroup(parseNumber(d[spgKey]));
      const spaceBetween = normalizeSpace(parseNumber(d[spaceKey]));
      const slidesOffsetAfter = normalizeSpace(parseNumber(d[offsetAfterKey]));
      const speed = resolveSpeedMs(d[speedKey], name);

      if (slidesPerView !== undefined) tier.slidesPerView = slidesPerView;
      if (slidesPerGroup !== undefined) tier.slidesPerGroup = slidesPerGroup;
      if (spaceBetween !== undefined) tier.spaceBetween = spaceBetween;
      if (slidesOffsetAfter !== undefined) tier.slidesOffsetAfter = slidesOffsetAfter;
      if (speed !== undefined) tier.speed = speed;
      if (Object.keys(tier).length) {
        breakpoints[width] = { ...breakpoints[width], ...tier };
      }
    });

    if (autoSlideSize) {
      options.slidesPerView = 'auto';
      options.slidesPerGroup = 1;
      options.slidesPerGroupAuto = autoSlidesPerGroup;

      Object.values(breakpoints).forEach((tier) => {
        tier.slidesPerView = 'auto';
        tier.slidesPerGroup = 1;
      });
    }

    if (Object.keys(breakpoints).length) options.breakpoints = breakpoints;
    return options;
  }

  function getSlideCount(el) {
    return el.querySelectorAll('.swiper-wrapper .swiper-slide').length;
  }

  function hasAutoSlidesPerView(options) {
    if (options.slidesPerView === 'auto') {
      return true;
    }

    return Object.values(options.breakpoints || {}).some((breakpoint) => breakpoint.slidesPerView === 'auto');
  }

  function getCurrentResponsiveOptions(options, width) {
    const current = { ...options };
    const breakpoints = Object.keys(options.breakpoints || {})
      .map((breakpoint) => Number(breakpoint))
      .filter((breakpoint) => Number.isFinite(breakpoint) && breakpoint <= width)
      .sort((a, b) => a - b);

    breakpoints.forEach((breakpoint) => {
      Object.assign(current, options.breakpoints[breakpoint]);
    });

    return current;
  }

  function getMeasuredSlidesPerView(el, options) {
    if (!hasAutoSlidesPerView(options)) {
      return undefined;
    }

    const slide = el.querySelector('.swiper-wrapper .swiper-slide');
    const slideWidth = slide && slide.getBoundingClientRect().width;
    const containerWidth = el.getBoundingClientRect().width || el.clientWidth;

    if (!slideWidth || !containerWidth) {
      return undefined;
    }

    const activeOptions = getCurrentResponsiveOptions(options, containerWidth);
    const spaceBetween = normalizeSpace(parseNumber(activeOptions.spaceBetween)) || 0;

    return Math.max(1, Math.floor((containerWidth + spaceBetween) / (slideWidth + spaceBetween)));
  }

  function getLargestSlidesPerView(options, measuredSlidesPerView) {
    const values = [options.slidesPerView];

    Object.values(options.breakpoints || {}).forEach((breakpoint) => {
      values.push(breakpoint.slidesPerView);
    });

    return values.reduce((largest, value) => {
      if (value === 'auto') return Math.max(largest, measuredSlidesPerView || 1);
      const number = parseNumber(value);
      return number === undefined ? largest : Math.max(largest, number);
    }, 1);
  }

  function getSwiperControls(el) {
    const section = el.closest('.shopify-section');
    const externalControls = el.id && section
      ? Array.from(section.querySelectorAll('[data-controls-for]')).find((controls) => (
          controls.dataset.controlsFor === el.id
        ))
      : null;
    const paginationEl = el.querySelector('.swiper-pagination')
      || (externalControls && externalControls.querySelector('.swiper-pagination'));
    const controlsContainer = el.closest('.floating-controls-container');
    const getSiblingControl = (selector) => {
      if (!controlsContainer) return null;
      return Array.from(controlsContainer.children).find((child) => child.matches(selector)) || null;
    };
    const nextEl = el.querySelector('.swiper-button-next')
      || getSiblingControl('.swiper-button-next')
      || (externalControls && externalControls.querySelector('.swiper-button-next'));
    const prevEl = el.querySelector('.swiper-button-prev')
      || getSiblingControl('.swiper-button-prev')
      || (externalControls && externalControls.querySelector('.swiper-button-prev'));
    const controls = {};

    if (paginationEl) {
      controls.pagination = { el: paginationEl, type: 'bullets', clickable: true };
    }

    if (nextEl && prevEl) {
      controls.navigation = { nextEl, prevEl };
    }

    return controls;
  }

  function resolveValue(value, context) {
    return typeof value === 'function' ? value(context) : value;
  }

  function buildOptions(el, overrides = {}) {
    const responsive = getResponsiveOptions(el);
    const slideCount = getSlideCount(el);
    const measuredSlidesPerView = getMeasuredSlidesPerView(el, responsive);
    const largestSlidesPerView = getLargestSlidesPerView(responsive, measuredSlidesPerView);
    const wantsLoop = parseBoolean(el.dataset.swiperLoop, false);
    const context = {
      el,
      responsive,
      slideCount,
      largestSlidesPerView,
      measuredSlidesPerView,
      wantsLoop,
    };
    const loopOverride = overrides.loop === undefined ? wantsLoop : overrides.loop;
    const loop = resolveValue(loopOverride, context);
    const options = {
      loop,
      touchEventsTarget: 'container',
      ...responsive,
      ...getSwiperControls(el),
    };

    if (el.dataset.swiperLoopAddBlankSlides !== undefined) {
      options.loopAddBlankSlides = parseBoolean(el.dataset.swiperLoopAddBlankSlides, false);
    }

    if (el.dataset.swiperWatchOverflow !== undefined) {
      options.watchOverflow = parseBoolean(el.dataset.swiperWatchOverflow, true);
    }

    applySlidesOffsetAfter(el, options, loop);

    return {
      ...options,
      ...(overrides.options || {}),
    };
  }

  /**
   * Product carousels start at opacity 0 (see custom.css) so they do not reflow
   * visibly from their pre-init layout, or pop in when deferred content is
   * injected. This is the signal that reveals them.
   *
   * The class is added on the next frame on purpose: adding it in the same task
   * that inserted or laid out the element gives the browser no painted starting
   * state, so the transition would be skipped and the fade would not happen.
   */
  function markCarouselReady(el) {
    if (!el || el.classList.contains('is-carousel-ready')) {
      return;
    }

    requestAnimationFrame(function () {
      el.classList.add('is-carousel-ready');
    });
  }

  function init(el, overrides = {}) {
    if (typeof Swiper === 'undefined' || !el) {
      return null;
    }

    if (el.swiper) {
      el.swiper.update();
      markCarouselReady(el);
      return el.swiper;
    }

    // Nothing to show, but never leave the element stuck at opacity 0.
    if (getSlideCount(el) === 0) {
      markCarouselReady(el);
      return null;
    }

    const swiper = new Swiper(el, buildOptions(el, overrides));
    markCarouselReady(el);
    el.dispatchEvent(new CustomEvent('maupets:swiper:initialized', {
      bubbles: true,
      detail: { swiper },
    }));
    return swiper;
  }

  function findAll(selector, root = document) {
    const carousels = root.matches && root.matches(selector)
      ? [root, ...root.querySelectorAll(selector)]
      : root.querySelectorAll(selector);

    return Array.from(carousels);
  }

  function initAll(selector, overrides = {}, root = document) {
    return findAll(selector, root).map((el) => init(el, overrides)).filter(Boolean);
  }

  const deferredSwiperOptions = new WeakMap();
  const observedSwipers = new WeakSet();
  let deferredSwiperObserver;

  function isProductPage() {
    return document.body && document.body.classList.contains('template-product');
  }

  function shouldDeferSwiper() {
    return isProductPage()
      && !(window.Shopify && window.Shopify.designMode)
      && 'IntersectionObserver' in window;
  }

  function getDeferredSwiperObserver() {
    if (deferredSwiperObserver) {
      return deferredSwiperObserver;
    }

    deferredSwiperObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        observer.unobserve(entry.target);
        delete entry.target.dataset.swiperDeferred;
        init(entry.target, deferredSwiperOptions.get(entry.target) || {});
        deferredSwiperOptions.delete(entry.target);
      });
    }, {
      rootMargin: '600px 0px',
      threshold: 0.01,
    });

    return deferredSwiperObserver;
  }

  function initOrDefer(el, overrides = {}) {
    if (!shouldDeferSwiper()) {
      return init(el, overrides);
    }

    if (el.swiper || observedSwipers.has(el) || getSlideCount(el) === 0) {
      return el.swiper || null;
    }

    observedSwipers.add(el);
    deferredSwiperOptions.set(el, overrides);
    el.dataset.swiperDeferred = 'true';
    getDeferredSwiperObserver().observe(el);
    return null;
  }

  function initAllManaged(selector, overrides = {}, root = document) {
    return findAll(selector, root).map((el) => initOrDefer(el, overrides)).filter(Boolean);
  }

  function initThemeSwipers(root = document) {
    initAllManaged(
      '.collection-filter__replacement-parts__wrapper.swiper',
      {
        options: {
          loopAddBlankSlides: false,
          lazyPreloadPrevNext: 2,
        },
      },
      root
    );
    initAllManaged(
      '.replacement-parts-section__carousel.swiper',
      {
        loop: ({ wantsLoop, slideCount, el }) => {
          const minSlides = parseNumber(el.dataset.swiperLoopMinSlides) ?? 4;
          return wantsLoop && slideCount >= minSlides;
        },
        options: {
          lazyPreloadPrevNext: 2,
        },
      },
      root
    );
    initAllManaged(
      '.logo-slider__swiper.swiper',
      {
        loop: ({ wantsLoop, slideCount, largestSlidesPerView }) => (
          wantsLoop && slideCount >= largestSlidesPerView * 2
        ),
        options: {
          loopAddBlankSlides: false,
          lazyPreloadPrevNext: 2,
     
        },
      },
      root
    );
    initAllManaged(
      '.collection-list-slider__swiper.swiper',
      {
        loop: ({ wantsLoop, slideCount, largestSlidesPerView }) => (
          wantsLoop && slideCount >= largestSlidesPerView * 2
        ),
        options: {
          loopAddBlankSlides: false,
          watchOverflow: true,
        },
      },
      root
    );
    initAllManaged(
      '.product-recommendations-prev__swiper.swiper, .recently-viewed-products-slider__swiper.swiper, .featured-collection-slider__swiper.swiper',
      {
        loop: ({ wantsLoop, slideCount, largestSlidesPerView }) => (
          wantsLoop && slideCount >= largestSlidesPerView * 2
        ),
        options: {
          loopAddBlankSlides: false,
          watchOverflow: true,
        },
      },
      root
    );
    initAllManaged(
      '.product-addons-slider__swiper.swiper',
      {
        loop: ({ wantsLoop, slideCount, largestSlidesPerView }) => (
          wantsLoop && slideCount >= largestSlidesPerView * 2
        ),
        options: {
          grabCursor: true,
          loopAddBlankSlides: false,
          simulateTouch: true,
          watchOverflow: true,
        },
      },
      root
    );
  }

  global.MaupetsSwiper = {
    buildOptions,
    getResponsiveOptions,
    getLargestSlidesPerView,
    init,
    initAll,
    initThemeSwipers,
  };

  global.swiperResponsiveOptionsFromDataset = getResponsiveOptions;
})(window);

(function() {
  function hydrateLocalizationOptions(toggle) {
    if (!toggle || !toggle.getAttribute) {
      return;
    }

    const popoverId = toggle.getAttribute('aria-controls');
    const popover = popoverId && document.getElementById(popoverId);
    const template = popover && popover.querySelector('template[data-localization-options-template]');
    if (!template) {
      return;
    }

    template.replaceWith(template.content.cloneNode(true));
  }

  function localizationToggleFromEvent(event) {
    const target = event.target;
    return target && target.closest ? target.closest('.localization-toggle') : null;
  }

  document.addEventListener('pointerdown', function(event) {
    hydrateLocalizationOptions(localizationToggleFromEvent(event));
  }, true);

  document.addEventListener('click', function(event) {
    hydrateLocalizationOptions(localizationToggleFromEvent(event));
  }, true);

  document.addEventListener('keydown', function(event) {
    if (!['Enter', ' ', 'ArrowDown'].includes(event.key)) {
      return;
    }
    hydrateLocalizationOptions(localizationToggleFromEvent(event));
  }, true);

  if (window.Shopify && window.Shopify.designMode) {
    document.querySelectorAll('.localization-toggle').forEach(hydrateLocalizationOptions);
  }

  class DeferredRecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      if (this._deferredObserver) {
        return;
      }

      const activate = () => {
        if (!this.isConnected) {
          return;
        }

        const recentlyViewed = document.createElement('recently-viewed-products');
        Array.from(this.attributes).forEach((attribute) => {
          recentlyViewed.setAttribute(attribute.name, attribute.value);
        });
        recentlyViewed.replaceChildren(...this.childNodes);
        this.replaceWith(recentlyViewed);
      };

      if ((window.Shopify && window.Shopify.designMode) || !('IntersectionObserver' in window)) {
        activate();
        return;
      }

      const section = this.closest('.shopify-section') || this;
      this._deferredObserver = new IntersectionObserver((entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }
        observer.disconnect();
        activate();
      }, {
        rootMargin: '600px 0px',
        threshold: 0.01,
      });
      this._deferredObserver.observe(section);
    }

    disconnectedCallback() {
      if (this._deferredObserver) {
        this._deferredObserver.disconnect();
        this._deferredObserver = null;
      }
    }
  }

  if (!window.customElements.get('deferred-recently-viewed-products')) {
    window.customElements.define('deferred-recently-viewed-products', DeferredRecentlyViewedProducts);
  }
})();

$(document).ready(function() {
  var productTabsAccordionSel = '.product-tabs .product__accordion';
  var productTabsSummarySel = '.product-tabs .product__accordion > summary';

  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (!t.closest(productTabsSummarySel)) return;
      e.preventDefault();
    },
    true
  );

  document.addEventListener(
    'keydown',
    function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var t = e.target;
      if (!t || !t.closest) return;
      if (t !== t.closest(productTabsSummarySel)) return;
      e.preventDefault();
    },
    true
  );

  $(document).on('click', productTabsAccordionSel + ' summary a', function (e) {
    e.preventDefault();
    e.stopPropagation();
    var $root = $(this).closest(productTabsAccordionSel);
    if (!$root.length) return;
    var el = $root[0];
    var $scope = $(productTabsAccordionSel);

    function closeOthers($keep) {
      $scope.not($keep).removeClass('active');
      $scope.not($keep).find('.accordion__content').slideUp();
      $scope.not($keep).each(function () {
        if (this.tagName === 'DETAILS') this.removeAttribute('open');
      });
    }

    if ($root.hasClass('active')) {
      $root.removeClass('active');
      if (el.tagName === 'DETAILS') el.removeAttribute('open');
      $root.find('.accordion__content').first().slideUp();
    } else {
      closeOthers($root);
      $root.addClass('active');
      if (el.tagName === 'DETAILS') el.setAttribute('open', '');
      $root.find('.accordion__content').first().slideDown();
    }
  });
 
 
   $('.accordian').click(function(){
 
     
     if($(this).hasClass('active')){
         $(this).removeClass('active');
 
     }
     else{
         $('.accordian').removeClass('active');
         $(this).addClass('active');
     }
 });
 
 
 // Get Variant Id 
 
 $(document).ready(function() {
   var newsletterBlockButton = document.querySelectorAll('.newsletter-block-signup.klaviyo_form_trigger');
 
  $(newsletterBlockButton).on('click', function(e){
   window._klOnsite = window._klOnsite || [];
   window._klOnsite.push(['openForm', 'SWKNA4']);
  });
 
 });
 
 
 $(document).ready(function() {
   // Attach event listener to a parent element
   $('body').on('click', '.ColorSwatch__Radio', function() {
     // Get data-variant-id from the clicked element
     var variantId = $(this).parent().children("input").data("variantId");
     var variantAvailable = $(this).parent().children("input").data("available");
     console.log(variantAvailable);
     // Find the button element relative to the clicked element
     var button = $(this).closest('.ProductItem__Info').find('button.ProductForm__AddToCart.product-item');
     var productItem = $(this).closest('.ProductItem__Wrapper');
     var label = '<div class="ProductItem__LabelList"><span class="ProductItem__Label ProductItem__Label--soldOut subtitle Heading Text--subdued">Out of Stock</span></div>'
     // Update the data-variant attribute after a short delay to allow for DOM updates
     setTimeout(function() {
       button.attr('data-variant', variantId);
       if (variantAvailable === false) {
         console.log("false", label);
         productItem.closest('.ProductItem__Wrapper').append(label);
         button.prop("disabled", true);
         button.text("Out of Stock");
       } else {
         button.prop("disabled", false);
         button.text("Add to Cart");
         productItem.find('.ProductItem__LabelList').remove();
       }
     }, 100);
   });
 });
 
 
 // Collection Add to Cart Button
 
 $(document).on('click', 'button.ProductForm__AddToCart.product-item', function() {
   var ID = $(this).attr("data-variant");
   addItemToCart(ID, 1);
 });
 function addItemToCart(variant_id, qty) {
   data = {
     "id": variant_id,
     "quantity": qty
   }
   jQuery.ajax({
     type: 'POST',
     url: '/cart/add.js',
     data: data,
     dataType: 'json',
     success: function() { 
       document.documentElement.dispatchEvent(new CustomEvent('product:added', {
         bubbles: true,
         detail: {
           variant: variant_id,
           quantity: qty
         }
       }));
       
     }   
     
   }); 
  
 }
 
 $('.ProductList.ProductList--grid.Grid').on('change', function() {
   $('button.ProductForm__AddToCart.product-item').click(function(){    
     var ID = $(this).attr("data-variant");
     addItemToCart( ID, 1);
   
   });
 });
});
 // Footer Accordion
 
 $(document).ready(function() {
   $('.Footer__Title').on('click', function(e){
     e.preventDefault();
 
     if($(this).parents('.Footer__Block').hasClass('active')){
       $(this).parents('.Footer__Block').removeClass('active');
       $('.Footer__Block .LinkList').slideUp();
     }else{
 
       $(this).parents('.Footer__Block').addClass('active');
       $('.Footer__Block .LinkList').slideUp().slideDown();
     }
   });
 });
 
 
 // Cart Dynamic Buttons
 
 window.onload = function () {
   var element = document.querySelector('.dynamic-checkout__content');
 
   if (element) {
       // check if the element has any child nodes
       if (element.childNodes.length === 0) {
           // hide the element if it has no child nodes
           element.style.display = 'none';
       }
   }
 };

$(document).ready(function(){
  // Keep the VO (bcpo) loader up until VO renders #bcpo; the fallback reveals the theme's picker if VO never loads
  if(document.documentElement.classList.contains('bcpo-pending')){
    var bcpoFallbackTimeout;

    var bcpoObserver = new MutationObserver(function() {
      if(document.getElementById('bcpo')){
        revealBundleOptions();
      }
    });

    var revealBundleOptions = function(){
      document.documentElement.classList.remove('bcpo-pending');
      bcpoObserver.disconnect();
      clearTimeout(bcpoFallbackTimeout);
    };

    if(document.getElementById('bcpo')){
      revealBundleOptions();
    } else {
      bcpoObserver.observe(document.body, {
        childList: true,
        subtree: true
      });

      bcpoFallbackTimeout = setTimeout(revealBundleOptions, 10000);
    }
  }



  // Video Updated
  let updatedVideoContainer = document.querySelector('.video-updated--content-video');
  if(updatedVideoContainer){
    let video = updatedVideoContainer.querySelector('video');
    let playButton = updatedVideoContainer.querySelector('.video-play--container');
    if(video){
      playButton.addEventListener('click', function(){
        video.play();
        playButton.classList.add('hide');
      });
    }
  }

  

  const eyes = document.querySelectorAll('.logo-eyes');

  function blinkEyes() {
    eyes.forEach(function(eye) {
      eye.style.transformBox = 'fill-box';
      eye.style.transformOrigin = 'center';

      if (typeof eye.animate === 'function') {
        eye.animate(
          [
            { transform: 'scaleY(1)' },
            { transform: 'scaleY(0.1)' },
            { transform: 'scaleY(1)' },
          ],
          { duration: 300, easing: 'ease-in-out' }
        );
      } else {
        eye.style.transform = 'scaleY(0.1)';
        setTimeout(function() {
          eye.style.transform = 'scaleY(1)';
        }, 150);
      }
    });
  }

  if (eyes.length) {
    blinkEyes();
    setInterval(blinkEyes, 3000);
  }

  let prevNextButton = document.querySelectorAll('carousel-next-button');
  prevNextButton.forEach(button => {
    button.addEventListener('click', function() {
      console.log('clicked');
    });
  });

  window.MaupetsSwiper.initThemeSwipers();


});

document.addEventListener('shopify:section:load', function(event) {
  window.MaupetsSwiper.initThemeSwipers(event.target);
});

document.addEventListener('maupets:async-products:loaded', function(event) {
  window.MaupetsSwiper.initThemeSwipers(event.target);
});

(function() {
  if (!document.body.classList.contains('template-index')) return;

  var lazyImageSelector = 'img[loading="lazy"]:not([aria-hidden="true"]):not([data-homepage-lazy-image])';

  function bindHomepageLazyImages(root) {
    var images = [];

    if (root.matches && root.matches(lazyImageSelector)) {
      images.push(root);
    }

    if (root.querySelectorAll) {
      images = images.concat(Array.prototype.slice.call(root.querySelectorAll(lazyImageSelector)));
    }

    images.forEach(function(image) {
      image.setAttribute('data-homepage-lazy-image', '');

      if (image.complete) {
        image.classList.add('homepage-lazy-image--loaded', 'homepage-lazy-image--skip-reveal');
        return;
      }

      function revealImage() {
        image.removeEventListener('load', revealImage);
        image.removeEventListener('error', revealImage);
        image.classList.add('homepage-lazy-image--loaded');
      }

      image.addEventListener('load', revealImage);
      image.addEventListener('error', revealImage);
    });
  }

  bindHomepageLazyImages(document);

  document.addEventListener('shopify:section:load', function(event) {
    bindHomepageLazyImages(event.target);
  });

  document.addEventListener('maupets:async-products:loaded', function(event) {
    bindHomepageLazyImages(event.target);
  });
})();
